import React from 'react';
import { ILegalTransactionMeeting } from '../../Interfaces/ILegalTransactionMeeting';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Check,
  Close,
  LocationOn,
  MeetingRoom,
  People,
  VideocamOutlined,
} from '@mui/icons-material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { LegalTransactionMeetingEmailOverview } from './Childs/LegalTransactionMeetingEmailOverview';
import { CustomeDateTimePicker } from '../../core/CustomeDateTimePicker';
import { castWithZeroTimezone } from '../../core/generic_helper';
import { ILegalTransactionMeetingEmail } from '../../Interfaces/ILegalTransactionMeetingEmail';
import { useGetLocations } from '../../hooks/reactQuerys/LocationQueries';
import {
  useGetRooms,
  useGetRoomOccupancy,
} from '../../hooks/reactQuerys/RoomQueries';
import { ILocation } from '../../Interfaces/ILocation';
import { IRoom } from '../../Interfaces/IRoom';
import {
  ILegalTransactionTalkRoomDraft,
  createTalkRoomDraft,
} from '../../Interfaces/ILegalTransactionTalkRoom';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { useGetAppArray } from '../../hooks/reactQuerys/AppQueries';
import { useCalendarConnections } from '../../hooks/reactQuerys/CalendarConnectionQueries';

interface Props {
  idLegalTransaction: number;
  idLegalTransactionType: number;
  legalClients: ILegalClient[];
  meetingObject: ILegalTransactionMeeting;
  setMeetingObject: (m: ILegalTransactionMeeting) => void;
  meetingEmailArray: ILegalTransactionMeetingEmail[];
  setMeetingEmailArray: (arr: ILegalTransactionMeetingEmail[]) => void;
  talkRooms: ILegalTransactionTalkRoomDraft[];
  setTalkRooms: React.Dispatch<
    React.SetStateAction<ILegalTransactionTalkRoomDraft[]>
  >;
  handleSave: () => void;
  setAddNew?: (flag: boolean) => void;
}

export const LegalTransactionMeetingEdit: React.FC<Props> = ({
  idLegalTransaction,
  idLegalTransactionType,
  legalClients,
  meetingObject,
  setMeetingObject,
  meetingEmailArray,
  setMeetingEmailArray,
  talkRooms,
  setTalkRooms,
  handleSave,
  setAddNew,
}) => {
  const { locations = [], isLoadingLocations } = useGetLocations();
  const { rooms, isLoadingRooms } = useGetRooms();
  const { appArray } = useGetAppArray();
  const nextcloudActive =
    appArray?.find(a => a.system_key === 'nextcloud')?.IsEffective === true;

  // Kalender-Verknuepfung (Phase 4): nur moeglich, wenn der aktuelle Benutzer
  // mindestens eine aktive Kalenderverbindung hat.
  const calendarUserId = Number(localStorage.getItem('idUser'));
  const { connections: calendarConnections } =
    useCalendarConnections(calendarUserId);
  const activeCalendarConnections = React.useMemo(
    () => (calendarConnections ?? []).filter(c => c.IsActive),
    [calendarConnections]
  );
  const calendarLinkEnabled = activeCalendarConnections.length > 0;
  const isLinkedToCalendar =
    meetingObject.linkToCalendar ?? !!meetingObject.CalendarEventId;
  const [isCreatingTalkRoom, setIsCreatingTalkRoom] = React.useState(false);
  const [newTalkRoomName, setNewTalkRoomName] = React.useState('');

  // Wer gerade aus dem Vorgang entfernt wurde, steht bis zum Speichern noch am
  // Termin; das Backend nimmt ihn dann aus kuenftigen Terminen heraus (#1343).
  const removedLegalClients = (meetingObject.LegalClientArray ?? []).filter(
    c => !legalClients.some(x => x.idLegalClient === c.idLegalClient)
  );

  const getLegalClientLabel = (c: ILegalClient) => {
    const name =
      [c.FirstName, c.LastName].filter(Boolean).join(' ').trim() ||
      c.Email ||
      `Mandant #${c.idLegalClient}`;
    return removedLegalClients.some(x => x.idLegalClient === c.idLegalClient)
      ? `${name} (nicht mehr im Vorgang)`
      : name;
  };

  const handleClose = () => setAddNew?.(false);

  const endTimeDisplay = React.useMemo(() => {
    const { Meeting_at, DurationInMin } = meetingObject;
    if (!Meeting_at || !DurationInMin || DurationInMin <= 0) return null;
    const end = new Date(
      castWithZeroTimezone(Meeting_at).getTime() + DurationInMin * 60000
    );
    return end.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [meetingObject.Meeting_at, meetingObject.DurationInMin]);

  const availableTalkRooms = talkRooms.filter(r => r._state !== 'deleted');

  const selectedLocation =
    meetingObject.idLocation != null
      ? (locations.find(l => l.idLocation === meetingObject.idLocation) ?? null)
      : null;

  const locationRooms = React.useMemo(
    () =>
      meetingObject.idLocation != null
        ? rooms.filter(
            room => room.Location_idLocation === meetingObject.idLocation
          )
        : [],
    [rooms, meetingObject.idLocation]
  );

  const selectedRoom =
    meetingObject.idRoom != null
      ? (locationRooms.find(room => room.idRoom === meetingObject.idRoom) ??
        null)
      : null;

  // Tag des Termins (YYYY-MM-DD) für die Belegungsabfrage des gewählten Raums.
  const meetingDate = meetingObject.Meeting_at
    ? (() => {
        const d = castWithZeroTimezone(meetingObject.Meeting_at);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })()
    : '';

  // Belegung nur abfragen, wenn Raum + Zeit feststehen.
  const { occupancy: roomOccupancy } = useGetRoomOccupancy(
    meetingObject.idRoom != null && !!meetingDate
      ? meetingObject.idLocation
      : null,
    meetingDate,
    meetingDate
  );

  // Doppelbelegung: überlappt der gewählte Raum im Zeitfenster einen anderen Termin?
  const roomConflict = React.useMemo(() => {
    if (
      meetingObject.idRoom == null ||
      !meetingObject.Meeting_at ||
      !meetingObject.DurationInMin
    ) {
      return null;
    }
    const start = castWithZeroTimezone(meetingObject.Meeting_at).getTime();
    const end = start + meetingObject.DurationInMin * 60000;
    const room = roomOccupancy.find(r => r.idRoom === meetingObject.idRoom);
    if (!room) return null;
    return (
      room.bookings.find(booking => {
        // Den gerade bearbeiteten Termin selbst nicht als Konflikt werten.
        if (
          booking.idLegalTransactionMeeting ===
          meetingObject.idLegalTransactionMeeting
        ) {
          return false;
        }
        const bStart = castWithZeroTimezone(booking.Meeting_at).getTime();
        const bEnd = bStart + (booking.DurationInMin ?? 0) * 60000;
        return start < bEnd && bStart < end; // Überlappung
      }) ?? null
    );
  }, [
    roomOccupancy,
    meetingObject.idRoom,
    meetingObject.Meeting_at,
    meetingObject.DurationInMin,
    meetingObject.idLegalTransactionMeeting,
  ]);

  const selectedTalkRoom =
    meetingObject.idLegalTransactionTalkRoom != null
      ? (availableTalkRooms.find(
          r =>
            r.idLegalTransactionTalkRoom ===
            meetingObject.idLegalTransactionTalkRoom
        ) ?? null)
      : null;

  const handleCreateTalkRoom = () => {
    if (!newTalkRoomName.trim()) return;
    const newRoom = createTalkRoomDraft({
      talkRooms,
      idLegalTransaction,
      roomName: newTalkRoomName,
    });
    setTalkRooms(prev => [...prev, newRoom]);
    setMeetingObject({
      ...meetingObject,
      idLegalTransactionTalkRoom: newRoom.idLegalTransactionTalkRoom,
      TalkRoomObject: newRoom,
    });
    setIsCreatingTalkRoom(false);
    setNewTalkRoomName('');
  };

  const isValid =
    !!meetingObject.Meeting_at &&
    !!meetingObject.LegalTransactionMeeting &&
    !!meetingObject.DurationInMin &&
    meetingObject.DurationInMin > 0;

  return (
    <Box>
      <Grid container spacing={2} alignItems='flex-start'>
        {/* Bezeichnung + Zeit in einer Zeile */}
        <Grid item xs={12} sm={5}>
          <CustomeTextField
            label='Terminbezeichnung'
            type='string'
            attr='LegalTransactionMeeting'
            object={meetingObject}
            setObject={setMeetingObject}
            required
            size='small'
            maxLength={45}
            error={!meetingObject.LegalTransactionMeeting}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CustomeDateTimePicker
            label='Beginn'
            dateValue={meetingObject.Meeting_at}
            setDateValue={value =>
              setMeetingObject({ ...meetingObject, Meeting_at: value ?? '' })
            }
            allowNull={false}
            allowPast={false}
          />
        </Grid>

        <Grid item xs={6} sm={2}>
          <TextField
            label='Dauer'
            size='small'
            type='number'
            fullWidth
            InputProps={{
              inputProps: { min: 0 },
              endAdornment: (
                <InputAdornment position='end'>Min.</InputAdornment>
              ),
            }}
            value={meetingObject.DurationInMin ?? ''}
            onChange={e =>
              setMeetingObject({
                ...meetingObject,
                DurationInMin:
                  Number(e.target.value) < 0 ? null : Number(e.target.value),
              })
            }
          />
        </Grid>

        <Grid
          item
          xs={6}
          sm={1}
          sx={{ display: 'flex', alignItems: 'center', pt: '22px !important' }}
        >
          {endTimeDisplay && (
            <Typography variant='body2' color='text.secondary' noWrap>
              – {endTimeDisplay}
            </Typography>
          )}
        </Grid>

        {/* Ort */}
        <Grid item xs={12}>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 1, mt: 0.5 }}
          >
            <Box sx={{ color: 'text.disabled', display: 'flex' }}>
              <LocationOn fontSize='small' />
            </Box>
            <Typography variant='caption' color='text.disabled'>
              Ort
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={locations}
                value={selectedLocation}
                loading={isLoadingLocations}
                getOptionLabel={(o: ILocation) =>
                  o.Location || [o.Street, o.StreetNr].filter(Boolean).join(' ')
                }
                isOptionEqualToValue={(o, v) => o.idLocation === v.idLocation}
                onChange={(_, v) =>
                  setMeetingObject({
                    ...meetingObject,
                    idLocation: v?.idLocation ?? null,
                    LocationObject: v ?? null,
                    // Raum gehoert zu genau einem Standort -> bei Wechsel leeren.
                    idRoom: null,
                    RoomObject: null,
                  })
                }
                clearOnEscape
                renderInput={params => (
                  <TextField {...params} label='Standort' size='small' />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={locationRooms}
                value={selectedRoom}
                loading={isLoadingRooms}
                disabled={meetingObject.idLocation == null}
                getOptionLabel={(o: IRoom) =>
                  o.Capacity != null
                    ? `${o.Name} (${o.Capacity} Pers.)`
                    : o.Name
                }
                isOptionEqualToValue={(o, v) => o.idRoom === v.idRoom}
                onChange={(_, v) =>
                  setMeetingObject({
                    ...meetingObject,
                    idRoom: v?.idRoom ?? null,
                    RoomObject: v ?? null,
                  })
                }
                clearOnEscape
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Raum'
                    size='small'
                    helperText={
                      meetingObject.idLocation == null
                        ? 'Erst einen Standort wählen.'
                        : locationRooms.length === 0
                          ? 'Für diesen Standort sind keine Räume hinterlegt.'
                          : undefined
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position='start'>
                            <MeetingRoom
                              fontSize='small'
                              sx={{ color: 'text.disabled' }}
                            />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {roomConflict && (
              <Grid item xs={12}>
                <Alert severity='warning' sx={{ py: 0 }}>
                  Dieser Raum ist im gewählten Zeitfenster bereits belegt:{' '}
                  <strong>{roomConflict.Title}</strong> (
                  {castWithZeroTimezone(roomConflict.Meeting_at).toLocaleString(
                    'de-DE',
                    {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}{' '}
                  Uhr).
                </Alert>
              </Grid>
            )}

            {nextcloudActive && (
              <Grid item xs={12} md={6}>
                <Stack direction='row' spacing={1} alignItems='flex-start'>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={availableTalkRooms}
                    value={selectedTalkRoom}
                    getOptionLabel={(o: ILegalTransactionTalkRoomDraft) =>
                      o.RoomName
                    }
                    isOptionEqualToValue={(o, v) =>
                      o.idLegalTransactionTalkRoom ===
                      v.idLegalTransactionTalkRoom
                    }
                    onChange={(_, v) =>
                      setMeetingObject({
                        ...meetingObject,
                        idLegalTransactionTalkRoom:
                          v?.idLegalTransactionTalkRoom ?? null,
                        TalkRoomObject: v ?? null,
                      })
                    }
                    clearOnEscape
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Online-Raum'
                        size='small'
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position='start'>
                                <VideocamOutlined
                                  fontSize='small'
                                  sx={{ color: 'text.disabled' }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Tooltip title='Neuen Online-Raum erstellen'>
                    <Button
                      variant='outlined'
                      size='small'
                      sx={{ minWidth: 0, px: 1.25, height: 40, mt: '1px' }}
                      onClick={() => {
                        setIsCreatingTalkRoom(c => !c);
                        if (isCreatingTalkRoom) setNewTalkRoomName('');
                      }}
                    >
                      <Add fontSize='small' />
                    </Button>
                  </Tooltip>
                </Stack>

                {isCreatingTalkRoom && (
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      label='Raumname'
                      size='small'
                      value={newTalkRoomName}
                      onChange={e => setNewTalkRoomName(e.target.value)}
                      fullWidth
                      autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleCreateTalkRoom();
                        if (e.key === 'Escape') {
                          setIsCreatingTalkRoom(false);
                          setNewTalkRoomName('');
                        }
                      }}
                    />
                    <Tooltip title='Anlegen'>
                      <span>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={handleCreateTalkRoom}
                          disabled={!newTalkRoomName.trim()}
                        >
                          <Check fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Abbrechen'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setIsCreatingTalkRoom(false);
                          setNewTalkRoomName('');
                        }}
                      >
                        <Close fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Grid>
            )}

            {calendarLinkEnabled && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isLinkedToCalendar}
                      onChange={e =>
                        setMeetingObject({
                          ...meetingObject,
                          linkToCalendar: e.target.checked,
                        })
                      }
                    />
                  }
                  label='Im Kalender eintragen'
                />
                {isLinkedToCalendar && activeCalendarConnections.length > 1 && (
                  <TextField
                    select
                    size='small'
                    label='Kalender'
                    sx={{ minWidth: 220, ml: 2 }}
                    value={meetingObject.idUserCalendarConnection ?? ''}
                    onChange={e =>
                      setMeetingObject({
                        ...meetingObject,
                        idUserCalendarConnection:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  >
                    {activeCalendarConnections.map(c => (
                      <MenuItem
                        key={c.idUserCalendarConnection}
                        value={c.idUserCalendarConnection}
                      >
                        {c.Label}
                        {c.Provider === 'webdav' ? ' · WebDAV' : ' · M365'}
                        {c.IsDefault ? ' (Standard)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Mandanten */}
        <Grid item xs={12}>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 1, mt: 0.5 }}
          >
            <Box sx={{ color: 'text.disabled', display: 'flex' }}>
              <People fontSize='small' />
            </Box>
            <Typography variant='caption' color='text.disabled'>
              Mandanten
            </Typography>
          </Stack>
          <Autocomplete
            multiple
            options={[...legalClients, ...removedLegalClients]}
            value={meetingObject.LegalClientArray ?? []}
            getOptionLabel={getLegalClientLabel}
            isOptionEqualToValue={(o, v) => o.idLegalClient === v.idLegalClient}
            onChange={(_, v) =>
              setMeetingObject({ ...meetingObject, LegalClientArray: v })
            }
            disableCloseOnSelect
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.idLegalClient}
                  label={getLegalClientLabel(option)}
                  size='small'
                  variant='outlined'
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                size='small'
                placeholder={
                  (meetingObject.LegalClientArray ?? []).length === 0
                    ? 'Mandanten auswählen…'
                    : ''
                }
                helperText='Nur diese Mandanten erhalten terminbezogene E-Mails.'
              />
            )}
          />
        </Grid>
      </Grid>

      {/* ── Termin-E-Mails ─────────────────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />

      <LegalTransactionMeetingEmailOverview
        idLegalTransaction={idLegalTransaction}
        idLegalTransactionType={idLegalTransactionType}
        idLegalTransactionMeeting={meetingObject.idLegalTransactionMeeting}
        meetingEmailArray={meetingEmailArray}
        setMeetingEmailArray={setMeetingEmailArray}
      />

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <Divider sx={{ mt: 3, mb: 2 }} />
      <Stack direction='row' justifyContent='flex-end' spacing={1.5}>
        <Button variant='outlined' color='inherit' onClick={handleClose}>
          Abbruch
        </Button>
        <Button variant='contained' disabled={!isValid} onClick={handleSave}>
          Übernehmen
        </Button>
      </Stack>
    </Box>
  );
};
